"use client"

import { useMemo, useState, type ReactNode } from "react"
import { AlertTriangle } from "lucide-react"
import {
  SectionRule,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { REFRIGERATOR_LIFESPAN } from "@/lib/refrigerator-answer-facts"
import { ENERGY_STAR_PLANNING_LIFETIME_YEARS } from "@/lib/tools/refrigerator-lifespan"

// ---------- Serial-number decoders ----------
// Sources: Manufacturer service documentation (publicly available).
// Logic preserved verbatim from the legacy calculator.

const BRAND_DECODERS: Record<
  string,
  { label: string; decode: (serial: string) => { year: number; month?: number } | null; hint: string }
> = {
  whirlpool: {
    label: "Whirlpool",
    hint: "Serial starts with a letter (year) + 2-digit week. Example: F1234567 (F = 2006).",
    decode: (serial) => {
      const yearCodes: Record<string, number> = {
        C: 2002, D: 2003, E: 2004, F: 2006, G: 2007, H: 2008,
        J: 2009, K: 2010, L: 2011, M: 2012, N: 2013, P: 2014,
        R: 2015, S: 2016, T: 2017, U: 2018, V: 2019, W: 2020,
        X: 2021, Y: 2022,
      }
      const firstChar = serial.charAt(0).toUpperCase()
      const year = yearCodes[firstChar]
      if (year === undefined) return null
      const week = parseInt(serial.substring(1, 3))
      if (isNaN(week) || week < 1 || week > 53) return { year }
      const month = Math.ceil(week / 4.33)
      return { year, month: Math.min(month, 12) }
    },
  },
  kitchenaid: {
    label: "KitchenAid",
    hint: "Same format as Whirlpool. Serial starts with a letter (year) + 2-digit week.",
    decode: (serial) => BRAND_DECODERS.whirlpool.decode(serial),
  },
  maytag: {
    label: "Maytag",
    hint: "Same format as Whirlpool (owned by Whirlpool Corp). Letter + week.",
    decode: (serial) => BRAND_DECODERS.whirlpool.decode(serial),
  },
  ge: {
    label: "GE / GE Profile",
    hint: "First two letters encode month and year. Example: AF = January 2006.",
    decode: (serial) => {
      const monthCodes: Record<string, number> = {
        A: 1, B: 2, C: 3, D: 4, E: 5, F: 6,
        G: 7, H: 8, J: 9, K: 10, L: 11, M: 12,
      }
      const yearCodes: Record<string, number> = {
        A: 2001, B: 2002, C: 2003, D: 2004, E: 2005, F: 2006,
        G: 2007, H: 2008, J: 2009, K: 2010, L: 2011, M: 2012,
        N: 2013, P: 2014, R: 2015, S: 2016, T: 2017, U: 2018,
        V: 2019, W: 2020, X: 2021, Y: 2022, Z: 2023,
      }
      const m = monthCodes[serial.charAt(0).toUpperCase()]
      const y = yearCodes[serial.charAt(1).toUpperCase()]
      if (m === undefined || y === undefined) return null
      return { year: y, month: m }
    },
  },
  samsung: {
    label: "Samsung",
    hint: "5th-6th digits of the serial encode the year and month. Example: R1234 5 67890 (5th digit = year).",
    decode: (serial) => {
      if (serial.length < 6) return null
      const yearDigit = parseInt(serial.charAt(4))
      if (isNaN(yearDigit)) return null
      const currentDecade = Math.floor(new Date().getFullYear() / 10) * 10
      let year = currentDecade + yearDigit
      if (year > new Date().getFullYear()) year -= 10
      const monthDigit = parseInt(serial.charAt(5))
      const month =
        !isNaN(monthDigit) && monthDigit >= 1 && monthDigit <= 9 ? monthDigit : undefined
      return { year, month }
    },
  },
  lg: {
    label: "LG",
    hint: "First digit = year of decade, next two digits = month. Example: 301 = 2013, January.",
    decode: (serial) => {
      const yearDigit = parseInt(serial.charAt(0))
      if (isNaN(yearDigit)) return null
      const currentDecade = Math.floor(new Date().getFullYear() / 10) * 10
      let year = currentDecade + yearDigit
      if (year > new Date().getFullYear()) year -= 10
      const monthStr = serial.substring(1, 3)
      const month = parseInt(monthStr)
      if (!isNaN(month) && month >= 1 && month <= 12) return { year, month }
      return { year }
    },
  },
  frigidaire: {
    label: "Frigidaire / Electrolux",
    hint: "First digit = year of decade, next two = week. Example: 342… = 2013, week 42.",
    decode: (serial) => {
      const yearDigit = parseInt(serial.charAt(0))
      if (isNaN(yearDigit)) return null
      const currentDecade = Math.floor(new Date().getFullYear() / 10) * 10
      let year = currentDecade + yearDigit
      if (year > new Date().getFullYear()) year -= 10
      const week = parseInt(serial.substring(1, 3))
      if (!isNaN(week) && week >= 1 && week <= 53) {
        return { year, month: Math.min(Math.ceil(week / 4.33), 12) }
      }
      return { year }
    },
  },
  bosch: {
    label: "Bosch / Thermador",
    hint: "FD code on the rating plate: FD YYMM. Example: FD 2108 = August 2021.",
    decode: (serial) => {
      const fdMatch = serial.match(/FD\s*(\d{2})(\d{2})/i)
      if (fdMatch) {
        const year = 2000 + parseInt(fdMatch[1])
        const month = parseInt(fdMatch[2])
        if (month >= 1 && month <= 12) return { year, month }
        return { year }
      }
      if (serial.length >= 4) {
        const year = 2000 + parseInt(serial.substring(0, 2))
        const month = parseInt(serial.substring(2, 4))
        if (year >= 2000 && year <= new Date().getFullYear()) {
          if (month >= 1 && month <= 12) return { year, month }
          return { year }
        }
      }
      return null
    },
  },
}

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
]

// Planning lifetime: the ENERGY STAR 12-year assumption the lifespan calculator
// cites (lib/tools/refrigerator-lifespan.ts, FRIDGE-902). It replaced a 13-year
// "AHAM / DOE" average that had no source (FRIDGE-1439). It is a planning figure,
// not a prediction; the 10-year replace cutoff is Rule 1 below.
const PLANNING_LIFETIME = ENERGY_STAR_PLANNING_LIFETIME_YEARS

// ---------- Tone logic ----------
// Hard rule: age >= 10 ⇒ replace (ENERGY STAR cutoff)

type Tone = "healthy" | "watch" | "replace"

interface DecodedResult {
  year: number
  month?: number
  age: number
  yearsLeft: number
  tone: Tone
  headline: string
  subline: string
  reasons: string[]
}

/** Exported for tests/tool-lifespan-claims-fridge-1439.test.ts, which checks the result copy. */
export function decodeAndScore(brandKey: string, serial: string): DecodedResult | null {
  const decoder = BRAND_DECODERS[brandKey]
  if (decoder === undefined) return null
  const decoded = decoder.decode(serial.trim())
  if (decoded === null) return null

  const now = new Date()
  const madeDate = new Date(decoded.year, (decoded.month || 6) - 1)
  const ageYears = (now.getTime() - madeDate.getTime()) / (365.25 * 24 * 60 * 60 * 1000)
  const age = Math.round(ageYears * 10) / 10
  const yearsLeft = Math.max(0, Math.round((PLANNING_LIFETIME - age) * 10) / 10)

  let tone: Tone
  let headline: string
  let subline: string
  let reasons: string[]

  // Rule 1 — hard 10-year ENERGY STAR replace cutoff
  if (age >= 10) {
    tone = "replace"
    headline = "Replace now."
    subline =
      "ENERGY STAR rule: refrigerators 10 years or older may use up to 35% more energy than newer ENERGY STAR certified models. Replacement payback is typically 2–4 years on energy alone."
    reasons = [
      `Manufactured ${decoded.month ? `${MONTHS[decoded.month - 1]} ` : ""}${decoded.year} — ${age} years old.`,
      "Past the 10-year ENERGY STAR replacement cutoff.",
      "Sealed-system components (compressor, evaporator, condenser) are aging in parallel.",
    ]
  } else if (age >= PLANNING_LIFETIME * 0.7) {
    tone = "watch"
    headline = "Plan ahead."
    subline = `About ${yearsLeft} year${yearsLeft !== 1 ? "s" : ""} left on the ${PLANNING_LIFETIME}-year planning lifetime ENERGY STAR uses. Start researching replacements — don't wait for a holiday-week breakdown.`
    reasons = [
      `Manufactured ${decoded.month ? `${MONTHS[decoded.month - 1]} ` : ""}${decoded.year} — ${age} years old.`,
      REFRIGERATOR_LIFESPAN.shortAnswer,
      "Approaching the 10-year ENERGY STAR cutoff.",
    ]
  } else {
    tone = "healthy"
    headline = "It's fine."
    subline = `About ${yearsLeft} year${yearsLeft !== 1 ? "s" : ""} left on the ${PLANNING_LIFETIME}-year planning lifetime ENERGY STAR uses. Keep up normal maintenance — vacuum the coils, replace the gasket every 8–10 years.`
    reasons = [
      `Manufactured ${decoded.month ? `${MONTHS[decoded.month - 1]} ` : ""}${decoded.year} — ${age} years old.`,
      `Comfortably under the 10-year ENERGY STAR cutoff.`,
      `~${yearsLeft} year${yearsLeft !== 1 ? "s" : ""} left on that planning lifetime, which is not a forecast of failure.`,
    ]
  }

  return { year: decoded.year, month: decoded.month, age, yearsLeft, tone, headline, subline, reasons }
}

const TONE_BORDER: Record<Tone, string> = {
  healthy: "#10a65c",
  watch: "#ff9951",
  replace: "#dc2626",
}
const TONE_BG: Record<Tone, string> = {
  healthy: "#ecfdf5",
  watch: "rgba(255,153,81,0.08)",
  replace: "#fef2f2",
}
const TONE_LABEL: Record<Tone, string> = {
  healthy: "Verdict · Healthy",
  watch: "Verdict · Watch",
  replace: "Verdict · Replace",
}
const TONE_CTA: Record<Tone, { label: string; href: string }> = {
  healthy: { label: "See maintenance tips →", href: "/tools/refrigerator-temperature-settings-guide" },
  watch: { label: "Browse 2026 models →", href: "/collections/refrigerators" },
  replace: { label: "Shop replacements →", href: "/collections/refrigerators" },
}

const RELATED = [
  {
    title: "Repair or Replace",
    category: "Specialty",
    description: "Apply the 50% repair rule to a quote.",
    href: "/tools/repair-or-replace-refrigerator",
    time: "~30 sec",
  },
  {
    title: "Refrigerator Lifespan Calculator",
    category: "Specialty",
    description: "Estimate years of life remaining.",
    href: "/tools/refrigerator-lifespan-calculator",
    time: "~45 sec",
  },
  {
    title: "Energy Cost Calculator",
    category: "Energy & Cost",
    description: "Annual operating cost for any fridge.",
    href: "/tools/energy-cost-calculator",
    time: "~30 sec",
  },
  {
    title: "Find Your Fridge",
    category: "Specialty",
    description: "Explore product groups based on your answers.",
    href: "/tools/find-your-fridge",
    time: "~2 min",
  },
]

const FAQS = [
  {
    question: "Where do I find my serial number?",
    answer:
      "Inside the fridge on the side wall, behind the crisper drawer, on the back exterior panel, behind the kick plate at the bottom, or on the door frame edge with the door open.",
  },
  {
    question: "Why is the cutoff exactly 10 years?",
    answer:
      "Per ENERGY STAR, refrigerators 10 years or older may use up to 35% more energy than newer ENERGY STAR certified models. Even if your unit still runs, a new one typically pays back in 2–4 years on electricity savings alone.",
  },
  {
    question: "What if I can't decode my serial?",
    answer:
      "Some models (older Bosch, KitchenAid pre-2002, gray-market units) use formats we don't cover. Look for a 'Date of Manufacture' field on the rating plate — it's often printed there directly.",
  },
  {
    question: "Does brand quality affect the cutoff?",
    answer:
      "Premium tiers (Sub-Zero, Thermador) commonly outlast 10 years mechanically, but ENERGY STAR's energy argument still applies. The compressor still uses more electricity than a 2026 unit. The 10-year rule holds regardless of brand.",
  },
  {
    question: "Is age the only thing that matters?",
    answer:
      "No — but it's the strongest signal. If your fridge is under 10 years old and shows warning signs (loud compressor, frost buildup, multiple repairs), use the Repair or Replace tool. If it's 10+, replace.",
  },
]

function InputLabel({ children, hint }: { children: ReactNode; hint?: string }) {
  return (
    <div className="mb-2">
      <label className="block text-[12px] font-bold uppercase tracking-[0.1em] text-gray-900">
        {children}
      </label>
      {hint && <span className="block text-[10px] text-gray-500 mt-0.5">{hint}</span>}
    </div>
  )
}

export function RefrigeratorAgeCalculatorPageClient() {
  const [brand, setBrand] = useState<string>("whirlpool")
  const [serial, setSerial] = useState<string>("")

  const verdict = useMemo(() => {
    if (brand === "" || serial.trim() === "") return null
    return decodeAndScore(brand, serial)
  }, [brand, serial])

  return (
    <ToolPageShell
      toolName="Refrigerator Age Calculator"
      heroEyebrow="Specialty"
      heroTitlePre="How old"
      heroTitleItalic="is your fridge?"
      heroSubtitle="Enter your brand and serial number — we decode the build date, calculate age, and apply the ENERGY STAR 10-year replace cutoff."
      heroMeta={["⏱ ~30 sec", "Manufacturer service docs · ENERGY STAR rule"]}
      heroShare={<ToolShareBar title="How old is your refrigerator?" />}
      faqs={FAQS}
      relatedTools={RELATED}
      cta={{
        title: "Past the 10-year cutoff?",
        body: "Answer five questions to explore a starting product group, with prices where available. Verify dimensions, features and warranty terms on each product page.",
        buttonLabel: "Find a replacement",
        buttonHref: "/tools/find-your-fridge",
      }}
    >
      {/* ============ INPUTS + VERDICT ============ */}
      <section className="py-8 md:py-12 bg-gray-50 border-b border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 grid lg:grid-cols-[460px_1fr] gap-8 lg:gap-12">
          {/* Inputs */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6 lg:sticky lg:top-24 lg:self-start">
            <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-5 text-[#ff9951]">
              Decode your serial number
            </div>

            <div className="space-y-6">
              <div>
                <InputLabel>Brand</InputLabel>
                <select
                  value={brand}
                  onChange={(e) => setBrand(e.target.value)}
                  className="tool-input-clean w-full h-11 px-3 border border-gray-300 rounded-lg bg-white text-[14px] font-semibold"
                >
                  {Object.entries(BRAND_DECODERS).map(([k, d]) => (
                    <option key={k} value={k}>
                      {d.label}
                    </option>
                  ))}
                </select>
              </div>

              {brand && BRAND_DECODERS[brand] && (
                <p className="text-[12px] text-gray-600 bg-gray-50 p-3 rounded-lg leading-[1.5]">
                  {BRAND_DECODERS[brand].hint}
                </p>
              )}

              <div>
                <InputLabel hint="Usually on the side wall, behind the crisper, or on the door frame">
                  Serial number
                </InputLabel>
                <input
                  type="text"
                  value={serial}
                  onChange={(e) => setSerial(e.target.value)}
                  placeholder="Enter serial from the rating plate"
                  className="tool-input-clean w-full h-11 px-3 border border-gray-300 rounded-lg bg-white text-[14px] font-semibold"
                />
              </div>

              {brand && serial.trim() && !verdict && (
                <div className="flex items-start gap-3 p-4 rounded-lg bg-orange-50 border border-orange-200">
                  <AlertTriangle className="w-5 h-5 text-[#ff9951] flex-shrink-0 mt-0.5" />
                  <p className="text-[13px] text-gray-700 leading-[1.5]">
                    Couldn&apos;t decode this serial. Double-check you&apos;ve copied it exactly from the rating plate — not the model number.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Verdict */}
          <div>
            {verdict && (
              <>
                <div
                  className="rounded-2xl p-6 sm:p-8 md:p-10 mb-6 md:mb-8"
                  style={{
                    background: TONE_BG[verdict.tone],
                    border: `1px solid ${TONE_BORDER[verdict.tone]}30`,
                  }}
                >
                  <div
                    className="text-[11px] font-bold uppercase tracking-[0.18em] mb-6 flex items-center gap-2"
                    style={{ color: TONE_BORDER[verdict.tone] }}
                  >
                    <span
                      className="w-2 h-2 rounded-full inline-block"
                      style={{ background: TONE_BORDER[verdict.tone] }}
                    />
                    {TONE_LABEL[verdict.tone]}
                  </div>
                  <div
                    className="text-gray-900 font-black"
                    style={{
                      fontSize: "clamp(44px, 7vw, 80px)",
                      letterSpacing: "-0.05em",
                      lineHeight: 0.95,
                      textWrap: "balance" as never,
                    }}
                  >
                    {verdict.headline}
                  </div>
                  <p
                    className="mt-5 md:mt-6 text-gray-700"
                    style={{ fontSize: 17, lineHeight: 1.45, textWrap: "pretty" as never }}
                  >
                    {verdict.subline}
                  </p>
                </div>

                <div className="grid grid-cols-3 border-y border-gray-300 divide-x divide-gray-300 mb-6 md:mb-8">
                  <div className="py-5 pr-5">
                    <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-gray-500 mb-1.5">
                      Manufactured
                    </div>
                    <div
                      className="font-black text-gray-900"
                      style={{ fontSize: 26, letterSpacing: "-0.03em", lineHeight: 1 }}
                    >
                      {verdict.month ? `${MONTHS[verdict.month - 1]} ` : ""}
                      {verdict.year}
                    </div>
                  </div>
                  <div className="py-5 px-5">
                    <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-gray-500 mb-1.5">
                      Age
                    </div>
                    <div
                      className="font-black"
                      style={{
                        fontSize: 30,
                        letterSpacing: "-0.03em",
                        color: TONE_BORDER[verdict.tone],
                      }}
                    >
                      {verdict.age}
                      <span className="text-[13px] font-bold text-gray-500 ml-1">yrs</span>
                    </div>
                  </div>
                  <div className="py-5 pl-5">
                    <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-gray-500 mb-1.5">
                      Years left
                    </div>
                    <div
                      className="font-black text-gray-900"
                      style={{ fontSize: 30, letterSpacing: "-0.03em" }}
                    >
                      {verdict.yearsLeft}
                      <span className="text-[13px] font-bold text-gray-500 ml-1">yrs</span>
                    </div>
                  </div>
                </div>

                <div className="mb-8">
                  <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-4 text-gray-900">
                    Why this verdict
                  </div>
                  <ul className="space-y-2">
                    {verdict.reasons.map((r) => (
                      <li
                        key={r}
                        className="flex items-start gap-3 p-4 rounded-lg bg-white border border-gray-200"
                      >
                        <div
                          className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-white"
                          style={{ background: TONE_BORDER[verdict.tone] }}
                        >
                          <AlertTriangle className="w-4 h-4" />
                        </div>
                        <p className="text-[14px] text-gray-700 leading-[1.55]">{r}</p>
                      </li>
                    ))}
                  </ul>
                </div>

                <a
                  href={TONE_CTA[verdict.tone].href}
                  className="block h-14 rounded-lg text-[15px] font-extrabold tracking-tight text-white inline-flex items-center justify-center w-full"
                  style={{
                    background: verdict.tone === "replace" ? "#ff9951" : "#111827",
                    boxShadow:
                      verdict.tone === "replace" ? "0 8px 22px rgba(255,153,81,0.35)" : undefined,
                  }}
                >
                  {TONE_CTA[verdict.tone].label}
                </a>
              </>
            )}
          </div>
        </div>
      </section>

      {/* ============ PRIMARY CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="If it's time"
          title="French door replacements"
          description="Wider shelves, bottom-freezer access, highest resale value."
          collectionType="french-door"
          fallbackCollectionType="bottom-freezer"
        />
      </section>

      {/* ============ METHODOLOGY ============ */}
      <section className="py-8 md:py-12 bg-gray-50 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <SectionRule eyebrow="How we decide" title="The methodology" />
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                n: "01",
                t: "Serial-number decoders",
                d: "Whirlpool, GE, Samsung, LG, Frigidaire, and Bosch each encode the build date differently. Decoders sourced from manufacturer service documentation.",
              },
              {
                n: "02",
                t: "Planning lifetime",
                d: `${PLANNING_LIFETIME} years, the refrigerator lifetime ENERGY STAR assumes in its savings comparison. Years left counts down from it as a planning figure, not a forecast of when a refrigerator fails.`,
              },
              {
                n: "03",
                t: "ENERGY STAR 10-year cutoff",
                d: "Refrigerators 10 years or older may use up to 35% more energy than newer ENERGY STAR certified models. Energy-savings payback is typically 2–4 years.",
              },
            ].map((m) => (
              <div key={m.n}>
                <div className="font-mono text-[11px] font-bold tracking-[0.15em] mb-3 text-[#ff9951]">
                  {m.n}
                </div>
                <h3 className="text-[22px] font-extrabold tracking-[-0.02em] mb-3 text-gray-900">
                  {m.t}
                </h3>
                <p className="text-[14px] text-gray-600 leading-[1.6]">{m.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ SECONDARY CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Side-by-side replacements"
          description="Eye-level freezer access and a slimmer footprint for tighter kitchens."
          collectionType="side-by-side"
          fallbackCollectionType="top-freezer"
        />
      </section>
    </ToolPageShell>
  )
}
